import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Drawer } from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';

import { useGetEntityById } from 'api/bitedb/entities/entities.hooks';
import { useGetDatabaseById } from 'api/bitedb/database/database.hooks';
import EntityVerificationReportView, {
  EntityVerificationReportSkeleton,
} from 'components/BiteDB/Components/EntityVerificationReportView';
import {
  buildEntityVerificationReportChecks,
  latestVerificationRun,
  VerificationRecordField,
} from 'components/BiteDB/Components/entityVerificationReportModel';
import DetailsCard, {
  DetailsCardHandles,
} from 'components/BiteDB/Cards/DetailsCard';
import DBModuleHeaderLayout from 'components/Common/Layouts/DBModuleHeaderLayout';
import { useVerificationRun } from 'components/BiteDB/Components/useVerificationRun';
import {
  downloadVerificationReport,
  reportPreparedBy,
} from 'components/BiteDB/Components/verificationReportPdf';
import { verificationRunBlockedReason } from 'components/BiteDB/Components/verificationReportSummary';
import CreditsAPI from 'api/CreditsAPI';
import { GlobalContext } from 'providers/GlobalProvider';

// The Entity Details input each verification field is edited in.
const DETAIL_FIELD_KEYS: Record<VerificationRecordField, string> = {
  name: 'name',
  country: 'country_of_origin',
  registration: 'business_registration_number',
  email: 'email',
  phone: 'phone',
  address: 'address',
  domain: 'domain',
};

const EntityVerificationReport: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { cur_user } = useContext(GlobalContext);
  const selectedTeamId = cur_user?.selected_team?.id;
  const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [focusField, setFocusField] = useState<VerificationRecordField | null>(
    null,
  );
  const detailsCardRef = useRef<DetailsCardHandles | null>(null);
  const isSavingRef = useRef(false);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);

  const {
    data: supplier,
    isLoading: isSupplierLoading,
    error: supplierError,
    refetch: refetchSupplier,
  } = useGetEntityById(
    id,
    Boolean(selectedTeamId),
    undefined,
    selectedTeamId ? String(selectedTeamId) : undefined,
  );
  const databaseId = supplier?.database_id ? String(supplier.database_id) : '';
  const { data: database, isLoading: isDatabaseLoading } = useGetDatabaseById(
    databaseId,
    Boolean(databaseId),
  );

  useEffect(() => {
    if (supplierError) {
      enqueueSnackbar('Failed to load the entity verification report.', {
        variant: 'error',
      });
    }
  }, [supplierError]);

  useEffect(() => {
    if (
      database?.group_id &&
      selectedTeamId &&
      String(database.group_id) !== String(selectedTeamId)
    ) {
      enqueueSnackbar(
        'You do not have permission to view this database. Switch to the correct team.',
        { variant: 'warning' },
      );
      history.replace('/bite-db/entities');
    }
  }, [database?.group_id, history, selectedTeamId]);

  // Open the drawer in edit mode, on the field the reviewer asked to fix.
  useEffect(() => {
    if (!isDetailsDrawerOpen) return undefined;
    let frame = 0;
    let tries = 0;
    const fieldKey = focusField ? DETAIL_FIELD_KEYS[focusField] : null;
    const focusInput = () => {
      const scope = '.entity-verification-edit-drawer';
      const input = document.querySelector<HTMLInputElement>(
        fieldKey
          ? `${scope} input[data-field="${fieldKey}"]`
          : `${scope} input`,
      );
      if (input) {
        input.focus();
        input.select();
        return;
      }
      // The inputs mount a frame or two after edit mode starts.
      tries += 1;
      if (tries < 20) frame = window.requestAnimationFrame(focusInput);
    };
    const timer = window.setTimeout(() => {
      detailsCardRef.current?.startEdit();
      frame = window.requestAnimationFrame(focusInput);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      window.cancelAnimationFrame(frame);
    };
  }, [isDetailsDrawerOpen, focusField]);

  const checks = useMemo(
    () => buildEntityVerificationReportChecks(supplier || {}),
    [supplier],
  );
  const lastRunAt = useMemo(() => latestVerificationRun(checks), [checks]);
  const verificationRun = useVerificationRun({
    supplier: supplier,
    entityKey: String(id),
    refetch: refetchSupplier,
  });
  // The team's Business Verification credits. Read again after each run (a run
  // uses credits); a failed read leaves the page neither showing nor blocking.
  const [verificationCredits, setVerificationCredits] = useState<{
    total: number;
    remaining: number;
  } | null>(null);
  useEffect(() => {
    if (
      !selectedTeamId ||
      !cur_user?.hasEntityAddon ||
      verificationRun.running
    ) {
      return undefined;
    }
    let cancelled = false;
    CreditsAPI.getVerificationCreditsSummary(String(selectedTeamId)).then(
      (response: any) => {
        if (cancelled) return;
        setVerificationCredits(
          response?.success
            ? {
                total: Number(response.result?.verification_credits ?? 0),
                remaining: Number(response.result?.remaining ?? 0),
              }
            : null,
        );
      },
    );
    return () => {
      cancelled = true;
    };
  }, [selectedTeamId, cur_user?.hasEntityAddon, verificationRun.running]);
  // The entity service runs verification only for a Team holding the Entity
  // Add-on, on a database whose verification setting is on. A Team without the
  // add-on never sees this page: it is sent back to the record, whose band
  // offers to contact sales. A refusal from the service corrects stale flags:
  // the Team is sent back too.
  const hasEntityAddon =
    Boolean(cur_user?.hasEntityAddon) &&
    verificationRun.refusal !== 'addon-required';
  const databaseVerificationOn =
    database?.entity_add_on_enabled !== false &&
    verificationRun.refusal !== 'database-off';

  if (!cur_user?.selected_team) {
    return <Redirect to='/' />;
  }

  const recordLocation = {
    pathname: `/supplier/${supplier?.id || supplier?.entity_id || id}`,
    state: databaseId ? { objectData: { viewType: 'Database' } } : undefined,
  };

  if (!hasEntityAddon) {
    return <Redirect to={recordLocation} />;
  }

  const closeDetailsDrawer = () => {
    detailsCardRef.current?.cancel();
    setIsDetailsDrawerOpen(false);
  };

  const downloadReport = async () => {
    if (!supplier || isDownloadingReport) return;
    setIsDownloadingReport(true);
    try {
      await downloadVerificationReport({
        supplier: supplier,
        databaseName: supplier?.database_name || database?.name,
        preparedBy: reportPreparedBy(cur_user),
      });
    } catch {
      enqueueSnackbar('The PDF report could not be created. Try again.', {
        variant: 'error',
      });
    } finally {
      setIsDownloadingReport(false);
    }
  };

  const openDetails = (field?: VerificationRecordField) => {
    setFocusField(field || null);
    setIsDetailsDrawerOpen(true);
  };

  return (
    <DBModuleHeaderLayout title='Business verification' hideTitleRow>
      {isSupplierLoading || (databaseId && isDatabaseLoading) ? (
        <EntityVerificationReportSkeleton />
      ) : !supplier ? (
        <div className='bv-page bv-page-empty'>
          <h1>This entity could not be found</h1>
          <p>
            It may have been removed, or it belongs to a team you are not
            viewing.
          </p>
          <button
            type='button'
            className='bv-btn bv-btn-ghost'
            onClick={() => history.push('/bite-db/entities')}
          >
            Back to BITE DB
          </button>
        </div>
      ) : (
        <EntityVerificationReportView
          supplier={supplier}
          databaseName={supplier?.database_name || database?.name || 'Database'}
          databaseId={databaseId || undefined}
          checks={checks}
          lastRunAt={lastRunAt}
          databaseVerificationOn={databaseVerificationOn}
          running={verificationRun.running}
          credits={verificationCredits}
          runBlockedReason={verificationRunBlockedReason({
            credits: verificationCredits,
            databaseScreening: verificationRun.databaseScreening,
          })}
          runStartedAt={verificationRun.runStartedAt}
          onBack={() => history.push(recordLocation)}
          onEditDetails={openDetails}
          onRun={verificationRun.run}
          onDownloadReport={downloadReport}
          downloadingReport={isDownloadingReport}
          onOpenDatabaseSettings={() =>
            history.push({
              pathname: `/database/${databaseId}`,
              search: `?tab=${encodeURIComponent('Screening Settings')}`,
            })
          }
        />
      )}

      <Drawer
        anchor='right'
        open={isDetailsDrawerOpen}
        onClose={closeDetailsDrawer}
        PaperProps={{ className: 'entity-hub-drawer-paper' }}
      >
        <div className='entity-hub-drawer-header'>
          <div>
            <h2>Entity Details</h2>
            <p>
              Update the fields used by verification for{' '}
              {supplier?.name || 'this entity'}.
            </p>
          </div>
          <button
            type='button'
            aria-label='Close details drawer'
            onClick={closeDetailsDrawer}
          >
            <CloseOutlinedIcon />
          </button>
        </div>
        <div className='entity-hub-drawer-body entity-verification-edit-drawer'>
          <div className='relationship-explorer-right'>
            <div className='relationship-card relationship-details-card'>
              {isEditingDetails ? (
                <div className='relationship-details-actions'>
                  <button
                    type='button'
                    className='create-new-team-btn relationship-details-action-btn'
                    onClick={closeDetailsDrawer}
                    disabled={isSavingDetails}
                  >
                    <div className='create-new-team-btn-text'>Cancel</div>
                  </button>
                  <button
                    type='button'
                    className='screening-btn relationship-details-action-btn'
                    onClick={() => detailsCardRef.current?.save()}
                    disabled={isSavingDetails}
                  >
                    {isSavingDetails ? 'Saving…' : 'Save Changes'}
                  </button>
                </div>
              ) : null}
              <DetailsCard
                ref={detailsCardRef}
                supplier={supplier}
                onEditStateChange={(editing: boolean) => {
                  setIsEditingDetails(editing);
                  // DetailsCard leaves edit mode mid-save only when the save
                  // succeeded; the record refetches through its mutation.
                  if (!editing && isSavingRef.current) {
                    setIsDetailsDrawerOpen(false);
                  }
                }}
                onSavingChange={(saving: boolean) => {
                  isSavingRef.current = saving;
                  setIsSavingDetails(saving);
                }}
                hideHeader
              />
            </div>
          </div>
        </div>
      </Drawer>
    </DBModuleHeaderLayout>
  );
};

export default EntityVerificationReport;
